import React from "react";
/** Элемент скроллится сам: ему разрешён скролл И контент выше рамки. */
export const isScrollHostBox = ({ overflowY, scrollHeight, clientHeight, }) => (overflowY === "auto" || overflowY === "scroll") && scrollHeight > clientHeight;
/**
 * Ближайший скроллящийся узел в цепочке предков (включая `start`).
 * Generic — чтобы покрыть тестом без DOM (компонентных тестов в проекте нет).
 *
 * `isBoundary` — граница документа (`body`/`html`). Дойдя до неё, возвращаем
 * null: у `body` в этом проекте `overflow-y: auto`, то есть по боксу он
 * выглядит скроллером, но прокрутку документа держит `scrollingElement`, а
 * `body.scrollTop` всегда 0. Без границы липкий CTA не появлялся бы уже на
 * обычном лендинге.
 */
export const findScrollHost = (start, parentOf, boxOf, isBoundary) => {
    let node = start ?? null;
    while (node) {
        if (isBoundary?.(node))
            return null;
        if (isScrollHostBox(boxOf(node)))
            return node;
        node = parentOf(node) ?? null;
    }
    return null;
};
/**
 * Куда прокрутить хост, чтобы секция встала к его верхней кромке.
 * `scrollMarginTop` секции (запас под липкую шапку кита) учитываем руками:
 * его соблюдает только `scrollIntoView`, а внутри контейнера мы ставим
 * `scrollTop` напрямую.
 */
export const anchorScrollTop = ({ elTop, hostTop, hostScrollTop, scrollMarginTop, }) => Math.max(0, elTop - hostTop + hostScrollTop - scrollMarginTop);
/** DOM-адаптер: от предков элемента вверх, сам элемент не рассматриваем. */
export const findDomScrollHost = (el) => findScrollHost(el?.parentElement ?? null, (node) => node.parentElement, (node) => ({
    overflowY: getComputedStyle(node).overflowY,
    scrollHeight: node.scrollHeight,
    clientHeight: node.clientHeight,
}), (node) => node === document.body || node === document.documentElement);
/** Скролл-хост страницы: null — скроллится документ (лендинг вне app-shell). */
export const LandingScrollHostContext = React.createContext(null);
export const useLandingScrollHost = () => React.useContext(LandingScrollHostContext);
/** Текущая прокрутка: хоста или документа. */
export const readScrollTop = (host) => host ? host.scrollTop : window.scrollY || document.documentElement.scrollTop || 0;
/** На кого вешать слушатель скролла (у контейнера события не всплывают). */
export const scrollEventTarget = (host) => host ?? window;
/** В начало страницы (переход между лендингами). */
export const scrollHostToTop = (host) => {
    if (host) {
        host.scrollTop = 0;
        return;
    }
    window.scrollTo(0, 0);
};
/**
 * Прокрутка к секции по id (in-page якорь, не навигация).
 * Документ — нативный `scrollIntoView` (умеет smooth и сам знает про
 * `scroll-margin-top`); контейнер — `scrollTop` напрямую.
 */
export const scrollHostToAnchor = (host, id) => {
    const el = document.getElementById(id);
    if (!el)
        return false;
    if (!host) {
        const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
        return true;
    }
    host.scrollTop = anchorScrollTop({
        elTop: el.getBoundingClientRect().top,
        hostTop: host.getBoundingClientRect().top,
        hostScrollTop: host.scrollTop,
        scrollMarginTop: parseFloat(getComputedStyle(el).scrollMarginTop) || 0,
    });
    return true;
};
